// ?use_ios_ui — twin of iOS 0.7.0 PollingOverlay / DownloadOverlay /
// LoadStatusOverlay: a scrim over the live AR view and a centred card.
// The error state offers Cancel and Retry — or, on a dead end, "Back to
// product page" in Retry's place, since re-polling cannot succeed.

import { AlertTriangle } from 'lucide-react'
import { IOS_UI_FONT, IOS_UI_PALETTE } from '@/components/ios-ui/ios-ui-palette/ios-ui-palette'
import { Spinner } from '@/components/spinner/spinner'

type TLoadStatusCardProps = {
  onRetry: () => void
  onCancel: () => void
  onBackToProductPage?: () => void
} & (
  | { kind: 'polling' }
  | { kind: 'download'; progress: number }
  | { kind: 'error'; errorTitle: string; error: string }
)

const SECONDARY = 'rgba(235, 235, 245, 0.6)'

export function LoadStatusCard(props: TLoadStatusCardProps) {
  return (
    <div
      data-testid="ios-load-card"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        fontFamily: IOS_UI_FONT,
        pointerEvents: 'auto',
        zIndex: 30,
      }}
    >
      <div
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: 32,
          borderRadius: 16,
          background: 'rgba(60, 60, 60, 0.55)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          textAlign: 'center',
          color: 'white',
        }}
      >
        <Content {...props} />
      </div>
    </div>
  )
}

function Content(props: TLoadStatusCardProps) {
  if (props.kind === 'error') {
    return (
      <>
        <AlertTriangle size={40} color="#FF9500" strokeWidth={1.75} />
        <div data-testid="ios-load-title" style={{ fontSize: 17, fontWeight: 600 }}>
          {props.errorTitle}
        </div>
        <div data-testid="ios-load-error" style={{ fontSize: 12, color: SECONDARY, maxWidth: 280 }}>
          {props.error}
        </div>
        <div style={{ display: 'flex', gap: 16, paddingTop: 8 }}>
          <button type="button" data-testid="ios-load-cancel" onClick={props.onCancel} style={plainButton}>
            Cancel
          </button>
          {props.onBackToProductPage != null ? (
            <button type="button" data-testid="ios-load-back" onClick={props.onBackToProductPage} style={prominentButton}>
              Back to product page
            </button>
          ) : (
            <button type="button" data-testid="ios-load-retry" onClick={props.onRetry} style={prominentButton}>
              Retry
            </button>
          )}
        </div>
      </>
    )
  }
  const title = props.kind === 'polling' ? 'Preparing Your Wheel' : 'Downloading Wheel...'
  return (
    <>
      <Spinner size={32} color={IOS_UI_PALETTE.accent} />
      <div data-testid="ios-load-title" style={{ fontSize: 17, fontWeight: 600 }}>
        {title}
      </div>
      {props.kind === 'polling' ? (
        <div style={{ fontSize: 12, color: SECONDARY }}>This may take a few seconds...</div>
      ) : (
        <>
          <div style={{ width: 200, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.2)', overflow: 'hidden' }}>
            <div style={{ width: `${Math.round(props.progress * 100)}%`, height: '100%', background: IOS_UI_PALETTE.accent }} />
          </div>
          <div data-testid="ios-load-percent" style={{ fontSize: 12, color: SECONDARY }}>
            {`${Math.round(props.progress * 100)}%`}
          </div>
        </>
      )}
    </>
  )
}

const plainButton = {
  border: 'none',
  background: 'transparent',
  color: SECONDARY,
  fontSize: 17,
  padding: '8px 12px',
  cursor: 'pointer',
} as const

const prominentButton = {
  border: 'none',
  borderRadius: 10,
  background: IOS_UI_PALETTE.accent,
  color: IOS_UI_PALETTE.onAccent,
  fontSize: 17,
  fontWeight: 600,
  padding: '8px 16px',
  cursor: 'pointer',
} as const
